import { Directive, ElementRef, OnInit, Output, EventEmitter, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MapsAPILoader } from '@agm/core';

declare var google: any;

@Directive({
    selector: '[google-place]'
})
export class GooglePlaceDirective implements OnInit, OnDestroy, OnChanges {
    @Input() radius: any;
    @Output() onSelect: EventEmitter<any> = new EventEmitter();

    private element: HTMLInputElement;
    private destroy$ = new Subject<void>();
    private isFromSuggestionSelection = false;
    private googleAutocomplete: any;

    constructor(
        elRef: ElementRef,
        private mapsAPILoader: MapsAPILoader
    ) {
        this.element = elRef.nativeElement;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.radius && this.googleAutocomplete) {
            this.initGooglePicker();
        }
    }

    ngOnInit() {
        this.initGooglePicker();
    }

    private initGooglePicker() {
        this.setupManualChangeListener();
        this.mapsAPILoader.load().then(() => {
            const options = this.radius ? this.setupGoogleOptions(this.radius) : {};
            this.googleAutocomplete = new google.maps.places.Autocomplete(this.element, options);

            google.maps.event.addListener(this.googleAutocomplete, 'place_changed', () => {
                const place = this.googleAutocomplete.getPlace();
                if (place) {
                    this.isFromSuggestionSelection = true;
                    this.onSelect.emit(place);
                    setTimeout(() => { this.isFromSuggestionSelection = false; }, 50);
                }
            });
        });
    }

    private setupGoogleOptions(radius: any) {
        const center = radius.center;
        if (!center) return {};
        const val = (radius.miles * 1.609344) / 100;

        const defaultBounds = {
            north: center.lat + val,
            south: center.lat - val,
            east: center.lng + val,
            west: center.lng - val,
        };

        return {
            bounds: defaultBounds,
            strictBounds: true
        };
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.googleAutocomplete) {
            google.maps.event.clearInstanceListeners(this.googleAutocomplete);
            this.googleAutocomplete = null;
        }
    }

    private setupManualChangeListener() {
        fromEvent(this.element, 'input')
            .pipe(
                debounceTime(100),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                setTimeout(() => {
                    if (this.isFromSuggestionSelection) return;
                    const currentAddress = this.element.value;
                    const mappedPlace = {
                        formatted_address: currentAddress || '',
                        name: currentAddress || '',
                        geometry: {
                            location: {
                                lat: () => 0,
                                lng: () => 0,
                                latitude: 0,
                                longitude: 0
                            }
                        },
                        address_components: this.tryExtractAddressComponents({ full_address: currentAddress || '' })
                    };
                    this.onSelect.emit(mappedPlace);
                }, 0);
            });
    }

    private tryExtractAddressComponents(item: any): any[] {
        const components = [];
        const zipMatch = item.full_address ? item.full_address.match(/\b\d{5}(?:-\d{4})?\b/) : null;
        if (zipMatch) {
            components.push({
                long_name: zipMatch[0],
                short_name: zipMatch[0],
                types: ['postal_code']
            });
        }
        return components;
    }
}
